import { Link } from "react-router-dom";
import { GraduationCap, BookOpen, Users, Lightbulb } from "lucide-react";
import { Reveal } from "../components/Reveal";

const PILLARS = [
  { icon: GraduationCap, label: "Higher Education Guidance" },
  { icon: BookOpen, label: "English Language Training" },
  { icon: Users, label: "Teacher & Professional Development" },
  { icon: Lightbulb, label: "Research & Academic Support" },
];

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-bone pt-[72px]" data-testid="about-page">
      {/* Hero */}
      <div className="relative overflow-hidden bg-forest-deep">
        <div className="bg-noise pointer-events-none absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <Reveal>
            <nav className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-100/50" data-testid="breadcrumb">
              <Link to="/" className="transition-colors hover:text-bone">Home</Link>
              <span>/</span>
              <span className="text-gold">About</span>
            </nav>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-6 max-w-3xl font-display text-4xl font-extrabold tracking-tight text-bone sm:text-5xl lg:text-6xl">
              About{" "}
              <em className="font-editorial font-medium italic text-gold">LearnYourWay</em>{" "}
              Consultants
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-emerald-100/60 sm:text-lg">
              An educational consultancy dedicated to empowering students, teachers, scholars, and researchers to achieve their full potential.
            </p>
          </Reveal>
        </div>
      </div>

      {/* Main content */}
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-14 lg:grid-cols-12">
          {/* About section */}
          <div className="space-y-14 lg:col-span-8">
            <Reveal>
              <section data-testid="about-mission-section">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold-dark">Who we are</p>
                <h2 className="mt-3 font-display text-3xl font-bold text-forest-deep sm:text-4xl">
                  LearnYourWay Consultants{" "}
                  <span className="font-editorial font-medium italic text-gold-dark">(LYW)</span>
                </h2>
                <div className="mt-6 space-y-5 text-base leading-relaxed text-foreground/75">
                  <p>
                    LearnYourWay Consultants (LYW) is an educational consultancy dedicated to empowering students, teachers, scholars, and researchers to achieve their full potential. We offer a diverse and comprehensive range of services — from higher education guidance and English language training to career development, teacher training, and research support — all under one roof.
                  </p>
                  <p>
                    Our mission is simple: to make expert academic and professional guidance accessible to every learner, regardless of their background or starting point.
                  </p>
                </div>

                <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-4" data-testid="pillars-grid">
                  {PILLARS.map(({ icon: Icon, label }) => (
                    <div key={label} className="flex flex-col items-start gap-3 rounded-2xl border border-border bg-white p-5">
                      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-forest/8">
                        <Icon className="h-5 w-5 text-forest" />
                      </span>
                      <span className="text-sm font-semibold leading-snug text-forest-deep">{label}</span>
                    </div>
                  ))}
                </div>
              </section>
            </Reveal>

            {/* Founder section */}
            <Reveal>
              <section data-testid="about-founder-section">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold-dark">Our Founder</p>
                <h2 className="mt-3 font-display text-3xl font-bold text-forest-deep sm:text-4xl">
                  Dr. Ayaz Afsar
                </h2>

                <div className="mt-8 flex flex-col gap-8 sm:flex-row">
                  <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-[1.5rem] bg-forest font-display text-3xl font-extrabold text-bone shadow-[0_12px_40px_rgba(10,41,30,0.18)]">
                    AA
                  </div>
                  <div className="space-y-4 text-base leading-relaxed text-foreground/75">
                    <p>
                      Dr. Ayaz Afsar is a Professor of English and Applied Linguist with over three decades of experience in higher education, academic leadership, and institutional development.
                    </p>
                    <p>
                      Holding a PhD and Master's from the University of Manchester, he is a former Vice President of Academics at the International Islamic University Islamabad. His expertise spans English language teaching, research training, curriculum development, and professional development — the very pillars on which LearnYourWay was founded.
                    </p>
                  </div>
                </div>

                <div className="mt-8 grid gap-3 sm:grid-cols-2">
                  {[
                    "PhD & Master's, University of Manchester",
                    "Former VP of Academics, IIUI",
                    "30+ Years in Higher Education",
                    "Applied Linguistics & English Language Teaching",
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-3 rounded-2xl border border-border bg-white px-5 py-4">
                      <span className="h-2 w-2 shrink-0 rounded-full bg-gold" />
                      <span className="text-sm font-medium text-forest-deep">{item}</span>
                    </div>
                  ))}
                </div>
              </section>
            </Reveal>
          </div>

          {/* Sidebar CTA */}
          <div className="lg:col-span-4">
            <Reveal delay={0.1}>
              <div className="rounded-[2rem] border border-border bg-white p-7 shadow-[0_25px_70px_rgba(10,41,30,0.09)] lg:sticky lg:top-24" data-testid="about-cta-card">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold-dark">Start here</p>
                <h3 className="mt-3 font-display text-2xl font-bold text-forest-deep">
                  Book a free strategy call
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  Talk to one of our advisors about your goals. Whether you're a student, teacher, or professional, we'll point you in the right direction — at no cost.
                </p>
                <Link
                  to="/"
                  data-testid="about-cta-btn"
                  className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-gold py-4 font-display text-sm font-bold text-forest-deep transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(212,175,55,0.4)]"
                >
                  Get started
                </Link>

                <div className="mt-7 space-y-3 border-t border-border pt-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Our services</p>
                  {["Higher Education Guidance", "Language Training", "Career Development", "Quranic Studies", "Online Tutoring"].map((s) => (
                    <p key={s} className="flex items-center gap-2.5 text-sm text-foreground/70">
                      <span className="h-1.5 w-1.5 rounded-full bg-gold-dark" />
                      {s}
                    </p>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  );
}
